import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { openDocument } from 'core/helpers/document-download';
import { invoicePathsForPolicy, waitForNewInvoice } from 'core/helpers/generated-invoice';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import useStyles from './send-invoice.styles';

/**
 * Staff "Send Invoice" billing-tab action (#34203): send the policy's current invoice to the
 * INSURED via the chosen channel(s) — email and/or a physical Lob letter. Nothing is sent to
 * lenders (LenderDock owns lender comms — #34004). A channel whose target is missing (no email /
 * no mailing address on file) is disabled with an explanatory note so an impossible send can't be
 * requested; when neither channel is possible the action is blocked entirely.
 *
 * "Generate invoice" (#36678) produces the same invoice, for the amount due now or the next
 * installment, without emailing or mailing it: the PDF is saved to the policy's documents and offered
 * here to view and print once it lands. It never charges, moves the schedule or sends a notice, and
 * needs no email or mailing address. With nothing due (a paid-in-full policy) both actions are off and
 * staff are pointed to the past invoices instead.
 */
const SendInvoice = observer(function SendInvoice({ className }) {
  const classes = useStyles();
  const toast = useToast();
  const store = useStore();
  const { isAgency } = useSession();
  const {
    account: {
      id: accountId,
      email,
      policies: {
        getDocuments,
        policy: { policy, policyDetails, billingDetails, sendPolicyInvoice }
      }
    }
  } = store;
  // The document list is read through the store on each poll, not destructured, so a refresh is seen.
  const readRegularDocuments = () => store.account.policies.documents?.regular;

  const hasEmail = !!email?.trim();
  const hasMailingAddress = !!policyDetails?.global?.mailingAddress?.address?.trim();
  // Nothing to invoice when there's no outstanding balance (e.g. a fully-paid policy). Guards against
  // creating a bogus $0 invoice on the backend's create path (#34203).
  const hasBalanceDue = Number(billingDetails?.totalRemaining) > 0;

  const [open, setOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(hasEmail);
  const [sendLetter, setSendLetter] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedInvoicePath, setGeneratedInvoicePath] = useState(null);
  // Stops the wait for a generated PDF when the modal closes or the component unmounts.
  const generationCancelled = useRef(false);
  useEffect(
    () => () => {
      generationCancelled.current = true;
    },
    []
  );

  const openModal = () => {
    // Default to the channels that are actually possible.
    setSendEmail(hasEmail);
    setSendLetter(!hasEmail && hasMailingAddress);
    setGeneratedInvoicePath(null);
    generationCancelled.current = false;
    setOpen(true);
  };

  const closeModal = () => {
    generationCancelled.current = true;
    setOpen(false);
  };

  const canSend = hasBalanceDue && ((sendEmail && hasEmail) || (sendLetter && hasMailingAddress));

  const handleSend = async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      const res = await sendPolicyInvoice({ policyId: policy.id, sendEmail, sendLetter });
      if (res?.success) {
        // The letter channel is dropped server-side if one was mailed to this policy in the last
        // 7 days (#34203); tell staff the letter was skipped so they aren't surprised.
        toast.notify(
          res.letterSkippedRecentlyMailed
            ? {
                type: 'info',
                message:
                  'A letter was already mailed to this policy in the last 7 days, so the letter was skipped. The email was sent.',
                durationInSeconds: 10
              }
            : { type: 'success', message: 'Invoice send requested. It can take a few minutes to be delivered.' }
        );
        closeModal();
      } else {
        toast.notify({ type: 'error', message: res?.error || 'Could not send the invoice.' });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: `An error occurred. ${error.message.replace(':', '').trim()}` });
    } finally {
      setSending(false);
    }
  };

  const handleGenerate = async () => {
    if (!hasBalanceDue) {
      return;
    }
    setGenerating(true);
    setGeneratedInvoicePath(null);
    try {
      // Reload first: the list may never have been loaded on this page, or be stale, and any invoice
      // missing from the snapshot would later be mistaken for the generated one.
      await getDocuments(accountId, isAgency);
      const knownPaths = invoicePathsForPolicy(readRegularDocuments(), policy.id);
      const res = await sendPolicyInvoice({
        policyId: policy.id,
        sendEmail: false,
        sendLetter: false,
        generateOnly: true
      });
      if (!res?.success) {
        toast.notify({ type: 'error', message: res?.error || 'Could not generate the invoice.' });
        return;
      }
      const path = await waitForNewInvoice({
        refresh: () => getDocuments(accountId, isAgency),
        readDocuments: readRegularDocuments,
        policyId: policy.id,
        knownPaths,
        isCancelled: () => generationCancelled.current
      });
      if (path) {
        setGeneratedInvoicePath(path);
      } else if (!generationCancelled.current) {
        toast.notify({
          type: 'info',
          message: 'The invoice is still being generated. It will appear in Documents in a few minutes.',
          durationInSeconds: 10
        });
      }
    } catch (error) {
      toast.notify({ type: 'error', message: `An error occurred. ${error.message.replace(':', '').trim()}` });
    } finally {
      setGenerating(false);
    }
  };

  const viewGeneratedInvoice = () => openDocument({ path: generatedInvoicePath, accountId, flow: 'generateInvoice' });

  return (
    <>
      {/* Rendered as a text link under "Add a payment hold"; the caller passes that link's class
          (payment.styles.js `holdPaymentButton`) so the two can't drift apart. */}
      <Button variant="text" color="secondary" className={className} onClick={openModal}>
        Send or Generate Invoice
      </Button>
      <BaseDialog open={open} onClose={closeModal} className={classes.dialog}>
        <div className={classes.content}>
          <Label type="h1" className={classes.title}>
            Invoice for the insured
          </Label>
          <Label type="body3" className={classes.subtitle}>
            Send the invoice for the amount due now, or the next installment if nothing is due yet. The invoice is only
            sent to the insured. Generate it instead to print it without sending anything.
          </Label>

          <div className={classes.options}>
            <Checkbox
              id="send-invoice-email"
              mode="light"
              label={hasEmail ? 'Email' : 'Email — no email address on file'}
              value={sendEmail}
              disabled={!hasEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <Checkbox
              id="send-invoice-letter"
              mode="light"
              label={hasMailingAddress ? 'Letter (mailed)' : 'Letter — no mailing address on file'}
              value={sendLetter}
              disabled={!hasMailingAddress}
              onChange={(e) => setSendLetter(e.target.checked)}
            />
          </div>

          {!hasBalanceDue && (
            <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
              <Label type="body3">Nothing due – reprint a past invoice from the invoice history.</Label>
            </NotificationCard>
          )}

          {hasBalanceDue && !hasEmail && !hasMailingAddress && (
            <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
              <Label type="body3">
                This policy has neither an email address nor a mailing address on file, so the invoice can’t be sent.
                Add contact details first.
              </Label>
            </NotificationCard>
          )}

          {generatedInvoicePath && (
            <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
              <div className={classes.generated}>
                <Label type="body3">The invoice is ready. Nothing was sent to the insured.</Label>
                <Button variant="text" color="secondary" onClick={viewGeneratedInvoice}>
                  View / Print Invoice
                </Button>
              </div>
            </NotificationCard>
          )}

          <div className={classes.actions}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleGenerate}
              disabled={!hasBalanceDue || generating || sending}
              loading={generating}
            >
              Generate Invoice
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSend}
              disabled={!canSend || sending || generating}
              loading={sending}
            >
              Send
            </Button>
          </div>
        </div>
      </BaseDialog>
    </>
  );
});

export default SendInvoice;
